import { motion } from "framer-motion";
import { LintMood } from "./LintMood";

const artifacts = [
  { n: "01", title: "Problem Framing", body: "What's in and what's out, decided calmly." },
  { n: "02", title: "Domain & Data Models", body: "Your data, mapped before it tangles." },
  { n: "03", title: "Architecture & Tech Choices", body: "Every pick, with the why behind it." },
  { n: "04", title: "API Surface Contracts", body: "How your services agree to talk." },
  { n: "05", title: "Deployment & Topology", body: "Where it runs, and how it grows." },
  { n: "06", title: "Non-Functional Reqs", body: "Security, auth and performance targets." },
  { n: "07", title: "Phased Delivery Plan", body: "A realistic path, in honest phases." },
  { n: "08", title: "Risk Register", body: "What could break, before it does." },
];

const pillars = [
  {
    title: "He pauses before code exists",
    body: "Most AI tools are eager juniors who start typing immediately. Lint stops, looks at the whole picture, and turns a vague idea into a clear, executable plan.",
  },
  {
    title: "He explains every choice",
    body: "No random stacks. If Lint picks PostgreSQL over MongoDB, he tells you why in plain words, measured against your scale, budget and deadline.",
  },
  {
    title: "He remembers the blueprint",
    body: "Lint keeps the architecture on record, so your coding agents can't quietly break the database or swap in random libraries behind your back.",
  },
];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

export function CtoLayer() {
  return (
    <section className="relative overflow-hidden bg-atmosphere py-20 sm:py-28" data-testid="cto-layer">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-brand">How Lint thinks</p>
          <h2 className="font-display mt-3 text-3xl font-semibold tracking-tight text-navy sm:text-5xl">
            Same idea. Better questions.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-ink-muted sm:text-lg">
            You bring the intent. Lint does the slow, senior thinking that
            engineers charge thousands for, then hands you eight artifacts
            before a single line of code.
          </p>
        </div>

        <div className="mt-14 grid items-stretch gap-6 lg:grid-cols-[0.9fr_auto_1.1fr]">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="flex flex-col justify-center rounded-3xl border border-border bg-surface-elevated p-6 sm:p-7"
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">You say</p>
            <p className="font-display mt-3 text-xl leading-snug text-navy">
              &ldquo;An Uber for dog walking that scales to 50k users.&rdquo;
            </p>
            <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-lint-red">Lint asks</p>
            <p className="font-display mt-2 text-base italic leading-snug text-brand">
              &ldquo;Who pays first, the owner or the walker? That decides your whole data model.&rdquo;
            </p>
          </motion.div>

          <div className="flex items-center justify-center">
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
              className="relative flex h-28 w-28 items-center justify-center rounded-full border border-border bg-sage-soft"
              data-testid="cto-layer-lint-core"
            >
              <motion.span
                aria-hidden
                animate={{ scale: [1, 1.25], opacity: [0.5, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                className="absolute inset-0 rounded-full border-2 border-sage"
              />
              <LintMood from="hero" to="deepDive" alt="Lint thinking" className="h-24 w-24" />
            </motion.div>
          </div>

          <motion.ol
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="grid gap-2.5 sm:grid-cols-2"
          >
            {artifacts.map((a) => (
              <motion.li key={a.n} variants={item} className="rounded-2xl border border-border bg-surface-elevated p-3.5">
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-sm font-bold text-sage">{a.n}</span>
                  <span className="text-sm font-semibold text-navy">{a.title}</span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-ink-muted">{a.body}</p>
              </motion.li>
            ))}
          </motion.ol>
        </div>

        <div className="mt-16 grid gap-5 lg:grid-cols-3">
          {pillars.map((p, index) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.45, delay: index * 0.1, ease: "easeOut" }}
              className="rounded-3xl border border-border bg-surface-elevated p-6"
              data-testid={`cto-pillar-${index}`}
            >
              <span className="font-display text-4xl font-semibold text-sage">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-2 text-lg font-semibold text-navy">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-muted">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
